import { UserRight } from "s2-rights";
import { createTemporaryId } from "../utils/model";
import { Country } from "./Country";
import { EnabledFeature, Feature } from "./Feature";
import UploadedFile from "./UploadedFile";
import User, { UsernameStyle } from "./User";
import UserPreferences from "./UserPreferences";
import { getCustomRights } from "../utils/rights";

const usernameStyles = Object.values(UsernameStyle);

/**
 * Represents the current user's organization.
 */
export class Organization {
  readonly id: string;
  /**
   * Changes after each organization update.
   */
  readonly key: string;
  readonly name: string;
  /**
   * Readable ID for resource lookup.
   */
  readonly simpleId: string;
  readonly identityNumber: string;
  readonly phone?: string;
  readonly website?: string;
  readonly logo?: UploadedFile;
  readonly email?: string;
  readonly smsSenderId?: string;
  readonly hidden: boolean = false;
  /**
   * Use hasFeature to get enabled features.
   */
  readonly features: EnabledFeature[] = [];
  readonly createdAt: Date;
  readonly usernameStyle: UsernameStyle;
  /**
   * List of countries in which the organization has business in.
   */
  readonly countries: Country[];
  readonly mainCountry: Country = Country.Sweden;
  readonly defaultLanguage?: string;
  readonly defaultUserPreferences: UserPreferences = new UserPreferences();
  readonly customRights: UserRight[];

  constructor(deriveFrom?: Partial<Organization>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    //  "Polar Mobil AB" => "polarmobil".
    this.simpleId = this.name ? this.name.split(' ', 2)
      .map(name => name.toLowerCase().replace(/[^a-z]/ig, ''))
      .filter(name => name !== "ab" && name !== "as")
      .join('') : null;

    if (!this.countries?.length) {
      this.countries = [Country.Sweden];
    }

    if (!this.mainCountry || !this.countries.includes(this.mainCountry)) {
      this.mainCountry = this.countries[0];
    }

    this.key = createTemporaryId();

    if ((deriveFrom as any)._id && !deriveFrom.id) {
      this.id = (deriveFrom as any)._id;
    }

    if (this.features) {
      this.features = this.features.map(feature => new EnabledFeature(feature));
    }

    this.createdAt = new Date(this.createdAt);

    const usernameStyleIndex = this.id.split('').reduce((index, character) =>
      index + character.charCodeAt(0), 0);

    this.usernameStyle = usernameStyles[usernameStyleIndex % usernameStyles.length];

    if (this.defaultUserPreferences) {
      this.defaultUserPreferences = new UserPreferences(this.defaultUserPreferences);
    }

    this.customRights = getCustomRights(this);
  }

  hasContactInfo() {
    return Boolean(this.phone || this.email || this.website);
  }

  private _hasFeature(feature: Feature) {
    return Boolean(this.features?.find(({ feature: otherFeature }) => otherFeature === feature));
  }

  hasFeature(feature: Feature) {
    if (feature === Feature.Contact && this._hasFeature(Feature.Dial)) {
      return true;
    }

    return this._hasFeature(feature);
  }

  hasCustomRight(user: User, right: UserRight) {
    return user.hasRight(right) || !this.customRights.includes(right);
  }

  /**
   * Returns a new, identical instance with the given feature.
   */
  withFeature(feature: Feature): Organization {
    return new Organization({ ...this, features: [...this.features, { since: new Date(), feature }] });
  }

  /**
   * Returns a new, identical instance without the given feature.
   */
  withoutFeature(feature: Feature): Organization {
    return new Organization({
      ...this,
      features: this.features.filter(({ feature: otherFeature }) => otherFeature !== feature),
    });
  }
}